import './style.scss';
import { useEffect, useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { InputText } from 'primereact/inputtext';
import { useLocation, useSearchParams } from 'react-router-dom';
import Pagination from 'components/Pagination';
import { deleteById, getAllForAdministration, reorder, restore } from 'apis/organizations.api';
import { Can } from 'components/Can';
import { mapError } from 'utils/func';
import Confirm from './components/Confirm';
import Form from './components/Form';

export default function OrganizationViewList() {
  // #region Data
  const { t } = useTranslation();
  const [isRowReorderable, setIsRowReorderable] = useState(false);
  const dialogFormRef = useRef(null);
  const dialogConfirmationRef = useRef(null);
  const { mutate: mutateDelete } = useMutation(deleteById);
  const { mutate: mutateRestore } = useMutation(restore);
  const { mutate: mutateReorder } = useMutation(reorder);
  const [searchParams] = useSearchParams({ replace: true });
  const location = useLocation();

  const [pagination, setPagination] = useState({
    first: parseInt(searchParams.get('first'), 10) || 0,
    rowsPerPage: 10,
  });

  const [filtersTable, setFiltersTable] = useState({
    _name: { value: null },
  });

  const [filters, setFilters] = useState({
    name: { value: null },
  });

  // applied filters, change every time click on Apply and Clear button for update
  const [appliedFilters, setAppliedFilters] = useState({
    name: { value: null },
  });

  const {
    data: _data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery(
    ['organizations-for-administration', pagination, appliedFilters],
    ({ queryKey: [, _pagination, _filters] }) => getAllForAdministration(_pagination, _filters)
  );

  const data = useMemo(
    () => _data?.data ?? { data: [], pagination: { row: 10, totalRecords: 0 } },
    [_data]
  );

  // #endregion

  // #region Event
  const onRowReorder = (e) => {
    if (JSON.stringify(e?.value) !== JSON.stringify(data)) {
      dialogConfirmationRef.current.open({
        mode: 'reorder',
        mutate: mutateReorder,
        value: e?.value?.map((item) => ({
          id: item.id,
          order: item.order,
        })),
      });
    }
  };

  useEffect(() => {
    const localFilters = JSON.parse(
      localStorage.getItem(`admin-${location.pathname.slice(1)}-filters`) ?? '{}'
    );

    setFilters({
      name: '',
      ...localFilters,
    });
    setAppliedFilters({
      name: '',
      ...localFilters,
    });

    // Set rows per page in localStorage
    const localRowsPerPage = parseInt(
      localStorage.getItem(`admin-${location.pathname.slice(1)}-rowsPerPage`),
      10
    );

    if (localRowsPerPage) {
      setPagination((cur) => ({ ...cur, rowsPerPage: localRowsPerPage }));
    }

    // Set current page (first) with searchParams
    const currentFirst = parseInt(searchParams.get('first'), 10) || 0;
    setPagination((cur) => ({ ...cur, first: currentFirst }));
  }, []);

  const handleClearFilter = (field) => {
    const newFilters = { ...filters, [field]: '' };
    setFilters(newFilters);
    setAppliedFilters(newFilters);
    localStorage.setItem(`admin-${location.pathname.slice(1)}-filters`, JSON.stringify(newFilters));
  };

  const handleApplyFilter = () => {
    setAppliedFilters(filters);
  };
  // #endregion

  // #region Render
  const formatActions = (value) => (
    <div className="flex justify-around gap-1">
      {value?.deletedAt === null ? (
        <>
          <Can do="update" on="organization">
            <Button
              severity="warning"
              tooltip={t('organization.update.title')}
              tooltipOptions={{ position: 'left' }}
              icon="pi pi-pencil"
              onClick={() => {
                dialogFormRef.current.open({
                  mode: 'UPDATE',
                  value,
                });
              }}
            />
          </Can>
          <Can do="delete" on="organization">
            <Button
              severity="danger"
              tooltip={t('organization.delete.title')}
              tooltipOptions={{ position: 'left' }}
              icon="pi pi-trash"
              onClick={() => {
                dialogConfirmationRef.current.open({
                  mode: 'delete',
                  mutate: mutateDelete,
                  value,
                });
              }}
            />
          </Can>
        </>
      ) : (
        <Can do="restore" on="organization">
          <Button
            icon="pi pi-undo"
            severity="danger"
            tooltip={t('organization.restore.title')}
            tooltipOptions={{ position: 'left' }}
            onClick={() =>
              dialogConfirmationRef.current.open({
                mode: 'restore',
                mutate: mutateRestore,
                value,
              })
            }
          />
        </Can>
      )}
    </div>
  );

  const organizationNameFilter = () => (
    <InputText
      value={filters.name}
      onChange={(e) => {
        const newFilters = { ...filters, name: e.target.value };
        setFilters(newFilters);
      }}
      placeholder={t('organization.name')}
      className="p-column-filter"
    />
  );

  const filterClearTemplate = (options) => (
    <Button
      type="button"
      label={t('employee.viewList.clear')}
      onClick={() => {
        options.filterClearCallback();
      }}
      severity="secondary"
      outlined
    />
  );

  const filterApplyTemplate = (options) => (
    <Button
      type="button"
      label={t('employee.viewList.apply')}
      onClick={() => {
        options.filterApplyCallback();
        localStorage.setItem(
          `admin-${location.pathname.slice(1)}-filters`,
          JSON.stringify(filters)
        );
      }}
      severity="success"
    />
  );

  const filterActiveStyle = (field) =>
    filters?.[field]?.length > 0 || Object.values(filters[field]).some((e) => e)
      ? { color: '#22C55E' }
      : { color: '#6c757d' };

  // #endregion Render

  return (
    <div className="card relative">
      {isError && <h3 className="text-red-600">{mapError(t, error)}</h3>}
      <div className="flex justify-content-between align-items-center pb-4">
        <h4 className="m-0 hidden sm:block text-xl sm:text-2xl">
          {t('organization.viewList.title')}
        </h4>
        <Can do="create" on="organization">
          <Button
            className="bg-cyan-600 border-cyan-600"
            label={t('organization.viewList.add')}
            icon="pi pi-plus"
            onClick={() => {
              dialogFormRef.current.open({
                mode: 'CREATE',
              });
            }}
          />
        </Can>
      </div>

      <div className="flex align-items-center mb-3">
        <Checkbox
          inputId="isRowReorderable"
          onChange={(e) => {
            setIsRowReorderable(e.checked);
            setPagination({ first: 0, rowsPerPage: 1000 });
            if (!e.checked) {
              const rowsPerPage = localStorage.getItem(
                `admin-${location.pathname.slice(1)}-rowsPerPage`
              );
              setPagination({
                first: 0,
                rowsPerPage: Number.isNaN(rowsPerPage) ? 10 : Number(rowsPerPage),
              });
            }
          }}
          checked={isRowReorderable}
        />
        <label htmlFor="isRowReorderable" className="ml-2">
          {t('organization.reorder.title')}
        </label>
      </div>

      <DataTable
        value={data?.data}
        className="p-datatable-gridlines header-table"
        showGridlines
        scrollable
        dataKey="id"
        loading={isLoading}
        responsiveLayout="scroll"
        emptyMessage={t('organization.viewList.emptyMessage')}
        reorderableRows
        onRowReorder={onRowReorder}
        filters={filtersTable}
        onFilter={(e) => {
          setFiltersTable(e.filters);
        }}
        filterDisplay="menu"
      >
        {isRowReorderable && (
          <Column className="text-center w-5rem" rowReorder={isRowReorderable} frozen />
        )}
        <Column
          header={t('organization.viewList.no')}
          field="order"
          className="text-center w-5rem"
          frozen
          sortable
        />
        <Column
          header={t('organization.name')}
          headerStyle={filterActiveStyle('name')}
          field="name"
          className="w-30rem"
          style={{ minWidth: '16rem' }}
          sortable
          filter
          filterField="_name"
          showFilterMatchModes={false}
          showFilterMenu
          filterElement={organizationNameFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          onFilterClear={() => handleClearFilter('name')}
          onFilterApplyClick={handleApplyFilter}
        />
        <Column header={t('organization.chief')} field="chief" style={{ minWidth: '14rem' }} />
        <Column header={t('organization.email')} field="email" className="w-10rem" />
        <Column header={t('organization.phone')} field="phone" className="w-10rem" />
        <Column header={t('organization.fax')} field="fax" className="w-10rem" />
        <Column
          header={t('organization.bankAccount')}
          field="bankAccount"
          className="w-10rem text-center"
        />
        <Column header={t('organization.bank')} field="bank" style={{ minWidth: '10rem' }} />

        <Column
          header={t('projectProposal.action')}
          body={formatActions}
          className="w-8rem text-center"
          frozen
          alignFrozen="right"
        />
      </DataTable>

      <Pagination
        pagination={pagination}
        setPagination={setPagination}
        paginationData={data?.pagination}
        pageName="organization"
      />

      <Form ref={dialogFormRef} refetch={refetch} />

      <Can do="delete" on="organization">
        <Confirm ref={dialogConfirmationRef} refetch={refetch} />
      </Can>
    </div>
  );
}
